'use client';

import Button from '@enlighten/design-system/dist/Button';
import Dialog from '@enlighten/design-system/dist/Dialog';
import Grid from '@enlighten/design-system/dist/Grid2';
import Typography from '@enlighten/design-system/dist/Typography';
import CancelSharp from '@mui/icons-material/CancelSharp';
import dayjs from 'dayjs';
import type { StaticImageData } from 'next/image';
import Image from 'next/image';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import Link from 'next/link';
import type { ComponentProps, PropsWithChildren } from 'react';
import { useState } from 'react';

import { isServer } from '@/constants/common';

interface PopupValue {
	/**
	 * @description 고유 아이디
	 *
	 * 다시 보지 않기 클릭 시 localStorage에 `${id}${startDate}${endDate}`를 key로 저장
	 * @example 'offday_EN_2023_08'
	 */
	id: string;
	/** 시작일 */
	startDate: string;
	/** 종료일 */
	endDate: string;
	/** 링크 */
	linkProps?: ComponentProps<typeof Link>;
	/** 이미지 */
	image: StaticImageData;
}

/** @todo Migrate to WordPress */
const popups: PopupValue[] = [
	{
		id: 'offday_EN_2023_08',
		startDate: '2023-08-14',
		endDate: '2023-08-14',
		image: require('@/assets/images/popups/offday_EN.png'),
	},
	{
		id: 'Thanksgiving_2023',
		startDate: '2023-09-18',
		endDate: '2023-10-03',
		image: require('@/assets/images/popups/Thanksgiving.png'),
	},
	{
		id: 'HappyNewYear',
		startDate: '2022-12-30',
		endDate: '2023-01-01',
		image: require('@/assets/images/popups/HappyNewYear.png'),
	},
	{
		id: 'WithNaver',
		startDate: '2022-12-01',
		endDate: '2023-01-31',
		linkProps: {
			href: '/re100',
		},
		image: require('@/assets/images/popups/WithNaver.png'),
	},
	{
		// 현충일 팝업
		id: 'offday_EN_2023_06',
		startDate: '2023-06-05',
		endDate: '2023-06-05',
		image: require('@/assets/images/popups/offday_EN_2023_06.png'),
	},
	{
		// 주주명부 공고 팝업
		id: 'shareholder',
		startDate: '2023-07-14',
		endDate: '2023-08-13',
		image: require('@/assets/images/popups/share_holder.png'),
	},
	{
		id: 'offday_EN_2023_09',
		startDate: '2023-09-01',
		endDate: '2023-09-01',
		image: require('@/assets/images/popups/offday_EN_09.png'),
	},
	{
		id: 'notice_2023-10-23',
		startDate: '2023-10-23',
		endDate: '2023-11-06',
		image: require('@/assets/images/popups/notice_2023-10-23.png'),
	},
	{
		id: 'notice_2023-11-25',
		startDate: '2023-11-28',
		endDate: '2023-12-14',
		linkProps: {
			href: '/projectseminar',
		},
		image: require('@/assets/images/popups/notice_2023-11-25.png'),
	},
	{
		id: 'offday_EN_2024_05',
		startDate: '2024-05-01',
		endDate: '2024-05-06',
		image: require('@/assets/images/popups/offday_en_2405.png'),
	},
	{
		id: 'offday_EN_2024_06',
		startDate: '2024-06-06',
		endDate: '2024-06-07',
		image: require('@/assets/images/popups/offday_EN_2024_06.png'),
	},
	{
		id: 'offday_EN_2024_08',
		startDate: '2024-08-15',
		endDate: '2024-08-17',
		image: require('@/assets/images/popups/offday_EN_2024_08.png'),
	},
	{
		id: 'warn_EN_2024_08',
		startDate: '2024-08-01',
		endDate: '2024-08-31',
		image: require('@/assets/images/popups/notice_warning_2024-08-31.png'),
	},
	{
		id: 'offday_EN_2024_09',
		startDate: '2024-09-11',
		endDate: '2024-09-22',
		image: require('@/assets/images/popups/offday_EN_2024_09_11_09_22.jpeg'),
	},
	{
		id: 'offday_EN_2024_10',
		startDate: '2024-09-26',
		endDate: '2024-10-06',
		image: require('@/assets/images/popups/offday_EN_2024_10_04.png'),
	},
	{
		id: 'offday_EN_2024_12',
		startDate: '2024-12-26',
		endDate: '2025-01-01',
		image: require('@/assets/images/popups/offday_EN_2024_12_24.png'),
	},
	{
		id: 'offday_EN_2025_01',
		startDate: '2025-01-22',
		endDate: '2025-02-02',
		image: require('@/assets/images/popups/offday_EN_2025_01_22.jpg'),
	},
	{
		id: 'offday_EN_2025_01',
		startDate: '2025-04-28',
		endDate: '2025-05-07',
		image: require('@/assets/images/popups/offday_EN_2025_05.png'),
	},
];

/** 홈페이지 공지 팝업 */
export default function NoticeModalProvider({ children }: PropsWithChildren) {
	const todayPopupValue = popups.find((popup) => {
		const now = dayjs();
		return now >= dayjs(popup.startDate) && now <= dayjs(popup.endDate);
	});

	const [open, setOpen] = useState<boolean>(!isServer ? !localStorage.getItem(`${todayPopupValue?.id}${todayPopupValue?.startDate}${todayPopupValue?.endDate}`) : true);

	return (
		<>
			{children}
			{todayPopupValue && (
				<Dialog
					open={open}
					sx={{
						marginX: 1,
						zIndex: 999998,
						'.MuiDialog-container': {
							alignItems: 'flex-start',
							paddingY: '100px',
							'.MuiPaper-root': {
								maxWidth: { xs: 312, md: 614 },
								width: '100%',
								borderRadius: '12px',
								margin: 0,
							},
						},
					}}
				>
					<Grid container width="100%" padding={{ xs: '12px 16px', md: '14px 20px' }}>
						<Grid flex={1}>
							<Button
								variant="text"
								onClick={() => {
									setOpen(false);
								}}
								sx={{
									height: 'fit-content',
									padding: 0,
									':hover': {
										bgcolor: 'transparent',
									},
									'.MuiSvgIcon-root': {
										color: 'black',
									},
								}}
							>
								<CancelSharp fontSize="small" />
								<Typography fontSize={{ xs: 12, md: 14 }} fontWeight={600} marginLeft={1}>
									닫기
								</Typography>
							</Button>
						</Grid>
						<Grid flex={1} textAlign="right">
							<Button
								variant="text"
								onClick={() => {
									const { id, startDate, endDate } = todayPopupValue;
									localStorage.setItem(`${id}${startDate}${endDate}`, 'true');
									setOpen(false);
								}}
								sx={{
									height: 'fit-content',
									padding: 0,
									':hover': {
										bgcolor: 'transparent',
									},
									'.MuiSvgIcon-root': {
										color: 'black',
									},
								}}
							>
								<CancelSharp fontSize="small" />
								<Typography fontSize={{ xs: 12, md: 14 }} fontWeight={600} marginLeft={1}>
									다시 보지 않기
								</Typography>
							</Button>
						</Grid>
					</Grid>
					{/*<Grid container justifyContent="flex-end" padding={{ xs: '12px 16px', md: '14px 20px' }}>*/}
					{/*	<Button*/}
					{/*		variant="text"*/}
					{/*		onClick={() => {*/}
					{/*			const { id, startDate, endDate } = todayPopupValue;*/}
					{/*			localStorage.setItem(`${id}${startDate}${endDate}`, 'true');*/}
					{/*			setOpen(false);*/}
					{/*		}}*/}
					{/*		sx={{*/}
					{/*			height: 'fit-content',*/}
					{/*			padding: 0,*/}
					{/*			':hover': {*/}
					{/*				backgroundColor: 'transparent'*/}
					{/*			},*/}
					{/*			'.MuiSvgIcon-root': {*/}
					{/*				color: 'black'*/}
					{/*			}*/}
					{/*		}}*/}
					{/*	>*/}
					{/*		<CancelSharp fontSize="small" />*/}
					{/*		<Typography fontSize={{ xs: 12, md: 14 }} fontWeight={600} marginLeft={1}>*/}
					{/*			다시 보지 않기*/}
					{/*		</Typography>*/}
					{/*	</Button>*/}
					{/*</Grid>*/}
					<Grid
						sx={{
							'& img': {
								width: '100%',
								height: 'auto',
							},
						}}
					>
						<Image src={todayPopupValue.image} alt={todayPopupValue.id} />
					</Grid>
				</Dialog>
			)}
		</>
	);
}
